
import axiosInstance from "./axiosInstance";

export interface AuthResponse {
  token: string;
  refreshToken: string;
  userRole: "Admin" | "Vendor" | "DeptUser" | "OrgAdmin" | "LocationAdmin" | "LeasedOrg" | string;
  userId: string;
  email: string;
}

export interface Signin {
  email: string;
  password: string;
}

export interface Register {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  userRole: string;
}

export interface verifyEmail {
  token: string;
}

export interface FrogotPassword {
  emailAddress: string;
}

export interface NewPassword {
  token: string;
  newPassword: string;
}

export interface User {
  leasedOrgId: any;
  token: any;
  userId: string;
  email: string;
  userRole: string;
  scope?: string | null;
  contactNumber: string | null;
  tenantId?: string | null;
  vendorId?: string | null;
  isActive: boolean;
  isEmailVerified: boolean;
  refreshToken?: string | null;
  refreshTokenExpiryTime?: string | null;
  firstName: string;
  lastName: string;
  profilePicture: string | null;
  createdOn: string;
  modifiedOn: string;
  role: "Admin" | "OrgUser" | "Vendor" | string;
  orgId: string | null;
  orgName: string | null;
  location: string | null;
  locationId: string | null;
  departmentId: string | null;
  departmentName: string | null;
  vendorName: string ;
}

export interface PaginatedUsersResponse {
  data: User[];
  totalCount: number;
}

export interface UserUpdatePayload {
  firstName?: string;
  lastName?: string;
  email?: string;
  userRole?: string;
  isActive?: boolean;
  scope?: string;
  contactNumber?: string;
  tenantId?: string;
  vendorId?: string;
  orgId?: string;
  orgName?: string;
  location?: string;
  locationId?: string;
  departmentId?: string;
  departmentName?: string;
}

export type ChangePasswordPayload = {
  email: string;
  oldPassword: string;
  newPassword: string;
};

export const authRepository = {
  Login: async (formData: Signin): Promise<AuthResponse> => {
    const { data } = await axiosInstance.post<AuthResponse>(
      "/api/Auth/login",
      formData
    );
    localStorage.setItem("jwt", data.token);
    localStorage.setItem("refreshToken", data.refreshToken);
    return data;
  },

  register: async (signup: Register): Promise<AuthResponse> => {
    const { data } = await axiosInstance.post<AuthResponse>(
      "/api/Auth/register",
      signup
    );
    localStorage.setItem("jwt", data.token);
    localStorage.setItem("refreshToken", data.refreshToken);
    return data;
  },

  verifyEmail: async (token: string): Promise<AuthResponse> => {
    const { data } = await axiosInstance.post<AuthResponse>(
      "/api/Auth/verify-email",
      { token }
    );
    localStorage.setItem("jwt", data.token);
    localStorage.setItem("refreshToken", data.refreshToken);
    return data;
  },

  forgotpassword: async (email: string): Promise<AuthResponse> => {
    const { data } = await axiosInstance.post<AuthResponse>(
      "/api/Auth/forget-password",
      { emailAddress: email }
    );
    localStorage.setItem("jwt", data.token);
    localStorage.setItem("refreshToken", data.refreshToken);
    return data;
  },

  resetpassword: async (newpassword: NewPassword): Promise<AuthResponse> => {
    const { data } = await axiosInstance.post<AuthResponse>(
      "/api/Auth/reset-password",
      newpassword
    );
    localStorage.setItem("jwt", data.token);
    localStorage.setItem("refreshToken", data.refreshToken);
    return data;
  },

  logout: () => {
    localStorage.clear()
  },

  RefreshToken: async (): Promise<string | null> => {
    const token = localStorage.getItem("jwt");
    const refreshToken = localStorage.getItem("refreshToken");

    if (!token || !refreshToken) return null;

    try {
      const { data } = await axiosInstance.post<{
        token: string;
        refreshToken: string;
      }>("/api/Auth/refresh", { token, refreshToken });

      const { token: newToken, refreshToken: newRefreshToken } = data;

      localStorage.setItem("jwt", newToken);
      localStorage.setItem("refreshToken", newRefreshToken);

      axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;

      return newToken;
    } catch (error) {
      authRepository.logout();
      return null;
    }
  },


  getUsersPaginated: async (
    pageNumber: number,
    pageSize: number,
    search?: string,
    roleFilter?: string
  ): Promise<PaginatedUsersResponse> => {
    try {
      const response = await axiosInstance.get<PaginatedUsersResponse>(
        "/api/users",
        {
          params: {
            pageNumber,
            pageSize,
            search,
            roleFilter,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return {
          data: [],
          totalCount: 0,
        };
      }
      throw error;
    }
  },

  getUserById: async (id: string): Promise<User> => {
    const { data } = await axiosInstance.get<User>(`/api/users/${id}`);
    return data;
  },

  createUser: async (payload: Partial<User>): Promise<User> => {
    const { data } = await axiosInstance.post<User>("/api/users", payload);
    return data;
  },

  updateUser: async (id: string, payload: UserUpdatePayload): Promise<User> => {
    const { data } = await axiosInstance.patch<User>(
      `/api/users/${id}`,
      payload
    );
    return data;
  },

  deleteUser: async (id: string): Promise<void> => {
    await axiosInstance.delete(`/api/users/${id}`);
  },

  changePassword: async (payload: ChangePasswordPayload): Promise<void> => {
    await axiosInstance.post("/api/auth/change-password", {
      email: payload.email,
      oldPassword: payload.oldPassword,
      newPassword: payload.newPassword,
    });
  },


  searchUsers: async (value: string): Promise<User[]> => {
    const { data } = await axiosInstance.get<User[]>(
      `/api/users/search-users`,
      { params: { value } }
    );
    return data;
  },

  uploadUserProfile: async (id: string, file: File): Promise<any> => {
    const formData = new FormData();
    formData.append("files", file);
    const { data } = await axiosInstance.post(`/api/users/${id}/upload-profile`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return data;
  },

  getPresignedProfileUrl: async (profileKey: string) => {
    const { data } = await axiosInstance.get(
      `/api/users/profile-by-id?profileKey=${encodeURIComponent(profileKey)}`
    );
    return data
  },

  deleteUserProfile: async (id: string, s3Key: string): Promise<void> => {
    await axiosInstance.delete(`/api/users/${id}/profile`, {
      params: { s3Key },
    });
  },

};